/**
 * BrandIdentityIndexView — index of every publication's brand identity.
 *
 * FEAT-048 (S-286). Route: /settings/brand. Same pattern as
 * VoiceProfilesIndexView — see that file for the full rationale.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, useApiQuery } from "@/lib/api";
import { useEnsureActiveWorkspace } from "@/lib/useActiveWorkspace";
import { SectionHeader } from "@/components/SectionHeader";
import {
  brandCompleteness,
  paletteSwatches,
  type BrandIdentityLike,
} from "@/lib/brand-completeness";
import { useSettingsWorkspaces } from "@/lib/useSettingsWorkspaces";

interface Agencyclient {
  id: number;
  name: string;
  slug: string;
}

interface Publication {
  id: number;
  name: string;
  slug: string;
  createdAt?: string;
  created_at?: string;
}

interface PublicationsResponse {
  data: Publication[];
  meta: { total: number; limit: number; offset: number };
}

const WS_KEY = "ccm:brand-identity:active_ws";

function fmtDate(iso: string | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

export function BrandIdentityIndexView() {
  const { data: workspaces, isLoading: wsLoading, error: wsError } =
    useSettingsWorkspaces<Agencyclient>();

  const [selectedId, setSelectedId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(WS_KEY);
    return raw ? Number(raw) : null;
  });

  useEffect(() => {
    if (selectedId != null) {
      try {
        window.sessionStorage.setItem(WS_KEY, String(selectedId));
      } catch {
        // sessionStorage may be unavailable in some browser configs.
      }
    }
  }, [selectedId]);

  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      const stillValid = workspaces.some((w) => w.id === selectedId);
      if (!stillValid) setSelectedId(workspaces[0].id);
    }
  }, [workspaces, selectedId]);

  useEnsureActiveWorkspace(selectedId);

  const pubsQ = useApiQuery<PublicationsResponse>(
    ["publications", selectedId],
    `/publications?agencyclient_id=${selectedId}`,
    { enabled: selectedId != null }
  );

  // S-375 — the index needs the identities themselves, not just the
  // publications that point at them, to show a palette and how much is set.
  const identitiesQ = useApiQuery<{ data: (BrandIdentityLike & { id: number })[] }>(
    ["brand-identities", selectedId],
    `/brand-identities?agencyclient_id=${selectedId}`,
    { enabled: selectedId != null }
  );

  if (wsLoading) {
    return (
      <div className="settings-page">
        <div className="state-loading">Loading brand identities…</div>
      </div>
    );
  }

  if (wsError) {
    return (
      <div className="settings-page">
        <div className="state-error">
          {wsError instanceof ApiError && wsError.status === 403
            ? "You don't have permission to view brand identities."
            : (wsError.message ?? "Could not load workspaces.")}
        </div>
      </div>
    );
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <div className="settings-page">
        <SectionHeader
          eyebrow="Settings · Brand Identity"
          title="Brand Identity"
          description="Manage visual identity per publication."
        />
        <div className="settings-empty">
          <h2>No workspaces yet</h2>
          <p>
            Create a workspace under Settings → Workspaces before adding
            publications.
          </p>
        </div>
      </div>
    );
  }

  const publications = pubsQ.data?.data ?? [];
  const identities = identitiesQ.data?.data ?? [];
  const identityFor = (pubId: number): (BrandIdentityLike & { id: number }) | null => {
    const pub = publications.find((p) => p.id === pubId) as
      | { brandIdentityId?: number | null; brand_identity_id?: number | null }
      | undefined;
    const bid = pub?.brandIdentityId ?? pub?.brand_identity_id ?? null;
    return bid == null ? null : (identities.find((i) => i.id === bid) ?? null);
  };
  const thinCount = publications.filter(
    (p) => brandCompleteness(identityFor(p.id)).thin
  ).length;

  return (
    <div className="settings-page">
      <SectionHeader
        eyebrow="Settings · Brand Identity"
        title="Brand Identity"
        description="Manage visual identity per publication."
      />

      <div className="team-toolbar">
        <span className="team-toolbar-label">Workspace</span>
        <select
          className="settings-select"
          value={selectedId ?? workspaces[0].id}
          onChange={(e) => setSelectedId(Number(e.target.value))}
        >
          {workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name} ({w.slug})
            </option>
          ))}
        </select>
      </div>

      {thinCount > 0 && (
        <div className="settings-warn-block">
          <strong>
            {thinCount === 1
              ? "One brand identity is nearly empty"
              : `${thinCount} brand identities are nearly empty`}
          </strong>
          <p>
            The Art Director prompts from these fields, so a publication with
            little filled in produces generic-looking images and reports no
            error. That is usually the answer to "why do its images come back
            generic".
          </p>
        </div>
      )}

      {pubsQ.isLoading ? (
        <div className="state-loading">Loading publications…</div>
      ) : publications.length === 0 ? (
        <div className="settings-empty">
          <h2>No publications yet</h2>
          <p>
            Create a publication under Settings → Publications to get a
            brand identity.
          </p>
        </div>
      ) : (
        <div className="idx-list">
          {publications.map((pub) => (
            <Link key={pub.id} to={`/settings/brand/${pub.id}`} className="idx-row">
              <span className="idx-icon" aria-hidden>
                ◈
              </span>
              <span className="idx-text">
                <span className="idx-name">{pub.name}</span>
                <span className="idx-sub">{pub.slug}</span>
              </span>
              {(() => {
                const identity = identityFor(pub.id);
                const c = brandCompleteness(identity);
                const swatches = paletteSwatches(identity?.primary_palette);
                return (
                  <>
                    <span className="brand-swatches" aria-hidden>
                      {swatches.length === 0 ? (
                        <span className="brand-swatch is-empty" title="No palette set" />
                      ) : (
                        swatches.slice(0, 6).map((sw) => (
                          <span
                            key={sw.role}
                            className="brand-swatch"
                            style={{ background: sw.color }}
                            title={`${sw.role} ${sw.color}`}
                          />
                        ))
                      )}
                    </span>
                    <span
                      className={`brand-complete ${c.thin ? "is-thin" : ""}`}
                      title={
                        c.missing.length
                          ? `Not set: ${c.missing.join(", ")}`
                          : "Every field is set"
                      }
                    >
                      <span className="brand-complete-bar">
                        <span style={{ width: `${(c.filled / c.total) * 100}%` }} />
                      </span>
                      <span className="brand-complete-n">
                        {c.filled} of {c.total}
                      </span>
                    </span>
                  </>
                );
              })()}
              <span className="idx-meta">{fmtDate(pub.createdAt ?? pub.created_at)}</span>
              <span className="idx-chevron" aria-hidden>
                ›
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
